// Theme module — drives the participant platform's light / dark mode.
//
// Modes: 'light' | 'dark' | 'auto' (follows OS prefers-color-scheme).
// Persistence: localStorage key `tappin_theme_mode`.
// Application: stamps `data-theme` on <body>, scoped to participant
// routes only — admin/utility views always render light.

const STORAGE_KEY = 'tappin_theme_mode';
const VALID_MODES = ['light', 'dark', 'auto'];

const PARTICIPANT_ROUTES = new Set([
  'lobby',
  'stage',
  'webapp',
  'sponsor',
  'live-stream',
  'program',
  'profile',
  // Networking section
  'dashboard',
  'ai-matches',
  'ai-match-sponsor',
  'matching-profile',
  'my-meetings',
  'open-sessions',
  'login',
  'login/otp',
  'login/otp/request',
  'login/pwa'
]);

function readStoredMode() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (VALID_MODES.includes(raw)) return raw;
  } catch (e) {
    /* localStorage unavailable */
  }
  return 'light';
}

function persistMode(mode) {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    /* ignore */
  }
}

function prefersDark() {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (e) {
    return false;
  }
}

function resolveTheme(mode, routeName) {
  if (!PARTICIPANT_ROUTES.has(routeName)) return 'light';
  if (mode === 'dark') return 'dark';
  if (mode === 'auto') return prefersDark() ? 'dark' : 'light';
  return 'light';
}

function stampBody(theme) {
  if (typeof document === 'undefined') return;
  document.body.setAttribute('data-theme', theme);
}

export default {
  namespaced: true,
  state: () => ({
    mode: readStoredMode(),
    currentRoute: null,
    appliedTheme: 'light',
    osListenerBound: false
  }),
  getters: {
    getMode: (s) => s.mode,
    getAppliedTheme: (s) => s.appliedTheme,
    isDark: (s) => s.appliedTheme === 'dark',
    isParticipantRoute: (s) => PARTICIPANT_ROUTES.has(s.currentRoute)
  },
  mutations: {
    setMode(state, mode) {
      state.mode = VALID_MODES.includes(mode) ? mode : 'light';
    },
    setCurrentRoute(state, routeName) {
      state.currentRoute = routeName || null;
    },
    setAppliedTheme(state, theme) {
      state.appliedTheme = theme === 'dark' ? 'dark' : 'light';
    },
    markOsListenerBound(state) {
      state.osListenerBound = true;
    }
  },
  actions: {
    apply({ state, commit }) {
      const theme = resolveTheme(state.mode, state.currentRoute);
      commit('setAppliedTheme', theme);
      stampBody(theme);
    },
    setMode({ commit, dispatch }, mode) {
      const next = VALID_MODES.includes(mode) ? mode : 'light';
      commit('setMode', next);
      persistMode(next);
      dispatch('apply');
    },
    routeChanged({ commit, dispatch }, routeName) {
      commit('setCurrentRoute', routeName);
      dispatch('apply');
    },
    bindOsListener({ state, commit, dispatch }) {
      if (state.osListenerBound) return;
      if (typeof window === 'undefined' || !window.matchMedia) return;
      try {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => {
          if (state.mode === 'auto') dispatch('apply');
        };
        if (typeof mq.addEventListener === 'function') {
          mq.addEventListener('change', handler);
        } else if (typeof mq.addListener === 'function') {
          mq.addListener(handler);
        }
        commit('markOsListenerBound');
      } catch (e) {
        /* matchMedia unavailable */
      }
    }
  }
};
